// 视频号 HP 黑客松 视频矩阵: 6 频道 × 12 视频, 反向给网站导流
import React, { useState, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import videosData from '../data/videos.json';
import { CURRICULUM, BRANCH_ORDER, getProblemBranchLevel } from '../lib/curriculum.js';
import { useI18n, t } from '../lib/i18n.js';
import { useDocumentTitle } from '../hooks/useDocumentTitle.js';

const { channels, videos } = videosData;

// 把 4 语言字段名按当前 lang 挑出来
function pickLocalized(obj, lang, base) {
  if (lang === 'zh-CN') return obj[base + '_zh'] || obj[base + '_en'] || '';
  if (lang === 'es-ES') return obj[base + '_es'] || obj[base + '_en'] || '';
  if (lang === 'ja-JP') return obj[base + '_ja'] || obj[base + '_en'] || '';
  return obj[base + '_en'] || obj[base + '_zh'] || '';
}

function formatDuration(sec) {
  if (sec >= 3600) return Math.floor(sec / 3600) + 'h ' + Math.round((sec % 3600) / 60) + 'min';
  if (sec >= 60) return Math.floor(sec / 60) + ':' + String(sec % 60).padStart(2, '0');
  return sec + 's';
}

export default function AcademyVideos() {
  const { lang } = useI18n();
  const isZh = lang === 'zh-CN';
  useDocumentTitle('🎬 ' + (isZh ? '视频号 HP 黑客松' : 'HP Hackathon Channel'));
  const [params, setParams] = useSearchParams();
  const focusChannel = params.get('channel') || '';
  const focusBranch = params.get('branch') || '';
  const [activeTab, setActiveTab] = useState('all');

  // 按 channel + branch 过滤
  const filtered = useMemo(() => {
    return videos.filter(v => {
      if (focusChannel && v.channel !== focusChannel) return false;
      if (focusBranch && !v.branches.includes(focusBranch) && !v.branches.includes('ALL')) return false;
      return true;
    });
  }, [focusChannel, focusBranch]);

  const setChannelFilter = (cid) => {
    const p = new URLSearchParams(params);
    if (cid) p.set('channel', cid); else p.delete('channel');
    setParams(p);
  };
  const setBranchFilter = (bid) => {
    const p = new URLSearchParams(params);
    if (bid) p.set('branch', bid); else p.delete('branch');
    setParams(p);
  };

  return (
    <div className="container-page py-8 max-w-7xl mx-auto">
      {/* Hero */}
      <section className="card bg-gradient-to-br from-pink-500/10 via-rose-500/5 to-orange-500/10 border-pink-500/20 mb-6">
        <div className="px-4 py-6 sm:px-8 sm:py-10">
          <div className="flex flex-wrap items-start gap-4">
            <div className="text-5xl sm:text-6xl">🎬</div>
            <div className="flex-1 min-w-0">
              <div className="text-xs uppercase tracking-wider text-pink-300 mb-1 font-mono">
                {isZh ? '微信视频号 · 微信搜索' : 'WeChat Channel · search'} "HP 黑客松"
              </div>
              <h1 className="text-3xl sm:text-4xl font-display font-extrabold gradient-text mb-2">
                {isZh ? '视频号 HP 黑客松' : 'HP Hackathon Channel'}
              </h1>
              <p className="text-slate-300 mb-4 max-w-2xl">
                {isZh
                  ? '每天 1 分钟, 点燃孩子的好奇心; 每周 1 个真实问题, 跟 5 个家庭一起攻. 视频号搜 "HP 黑客松" 看完整系列, 这里所有视频都能直接跳回 Academy 动手.'
                  : '1 min a day to spark curiosity; 1 real problem a week with 5 families. Search "HP 黑客松" on WeChat for full series, all videos link back to Academy.'}
              </p>
              <div className="flex flex-wrap gap-2">
                <span className="badge bg-pink-500/15 text-pink-200 border border-pink-500/30">
                  📱 {isZh ? '微信视频号' : 'WeChat Channel'}
                </span>
                <span className="badge bg-cyan-500/15 text-cyan-200 border border-cyan-500/30">
                  🌐 {isZh ? '4 语言' : '4 languages'}
                </span>
                <span className="badge bg-amber-500/15 text-amber-200 border border-amber-500/30">
                  🆓 {isZh ? '全部免费' : 'All free'}
                </span>
              </div>
            </div>
            {/* 二维码占位 (用户上传到视频号后替换 URL) */}
            <div className="flex-shrink-0 text-center">
              <div className="w-28 h-28 bg-white/95 rounded-lg flex items-center justify-center text-3xl border-2 border-pink-500/40 mx-auto">
                📱
              </div>
              <div className="text-xs text-slate-400 mt-2 font-mono">
                {isZh ? '视频号二维码' : 'QR code'}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 6 频道矩阵 */}
      <section className="mb-6">
        <h2 className="text-lg font-display font-bold mb-3 flex items-center gap-2">
          <span>📺</span>
          {isZh ? '6 频道矩阵' : '6-channel matrix'}
        </h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          {channels.map(ch => {
            const active = focusChannel === ch.id;
            const count = videos.filter(v => v.channel === ch.id).length;
            return (
              <button
                key={ch.id}
                onClick={() => setChannelFilter(active ? '' : ch.id)}
                className={`card text-left p-3 transition hover:scale-105 ${active ? 'ring-2 ring-pink-400' : ''}`}
              >
                <div className="text-2xl mb-1">{ch.icon}</div>
                <div className="text-sm font-bold mb-1 line-clamp-2">{pickLocalized(ch, lang, 'name')}</div>
                <div className="text-xs text-slate-400 mb-2 line-clamp-2">{pickLocalized(ch, lang, 'desc')}</div>
                <div className="flex items-center justify-between text-xs">
                  <span className="text-slate-500 font-mono">{count} {isZh ? '条' : 'videos'}</span>
                  <span className="text-slate-500">{ch.cadence}</span>
                </div>
              </button>
            );
          })}
        </div>
      </section>

      {/* 学科过滤 */}
      <section className="mb-6 flex flex-wrap items-center gap-2">
        <span className="text-sm text-slate-400 mr-2">{isZh ? '学科:' : 'Branch:'}</span>
        <button
          onClick={() => setBranchFilter('')}
          className={`badge ${!focusBranch ? 'bg-cyan-500/30 text-cyan-200' : 'bg-white/5 text-slate-400'} hover:bg-cyan-500/20`}
        >
          {isZh ? '全部' : 'All'}
        </button>
        {BRANCH_ORDER.map(bid => {
          const b = CURRICULUM.branches[bid];
          if (!b) return null;
          const active = focusBranch === bid;
          return (
            <button
              key={bid}
              onClick={() => setBranchFilter(active ? '' : bid)}
              className={`badge ${active ? 'bg-cyan-500/30 text-cyan-200' : 'bg-white/5 text-slate-400'} hover:bg-cyan-500/20`}
            >
              {b.icon} {isZh ? b.name_zh : b.name_en}
            </button>
          );
        })}
      </section>

      {/* 当前过滤提示 */}
      {(focusChannel || focusBranch) && (
        <div className="mb-4 text-sm text-slate-400 flex items-center gap-2">
          <span>🔍</span>
          {isZh ? `找到 ${filtered.length} 条视频` : `Found ${filtered.length} videos`}
          {focusChannel && (
            <span className="badge bg-pink-500/20 text-pink-200">
              {channels.find(c => c.id === focusChannel)?.icon} {pickLocalized(channels.find(c => c.id === focusChannel) || {}, lang, 'name')}
            </span>
          )}
          {focusBranch && (
            <span className="badge bg-cyan-500/20 text-cyan-200">
              {CURRICULUM.branches[focusBranch]?.icon} {isZh ? CURRICULUM.branches[focusBranch]?.name_zh : CURRICULUM.branches[focusBranch]?.name_en}
            </span>
          )}
        </div>
      )}

      {/* 视频网格 */}
      <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {filtered.length === 0 ? (
          <div className="col-span-full text-center text-slate-500 py-20">
            <div className="text-4xl mb-2">🎬</div>
            {isZh ? '没有匹配的视频, 试试别的频道或学科' : 'No videos match, try other channel/branch'}
          </div>
        ) : (
          filtered.map(v => {
            const ch = channels.find(c => c.id === v.channel);
            return (
              <div key={v.id} className="card overflow-hidden hover:border-pink-500/30 transition group">
                {/* 缩略图 (v1.7.1: 真实视频用 <video>, 占位用 icon) */}
                {v.video_demo ? (
                  <div className="aspect-video bg-slate-900 relative overflow-hidden">
                    <video
                      src={v.video_url}
                      className="absolute inset-0 w-full h-full object-cover"
                      controls
                      preload="metadata"
                      playsInline
                      poster=""
                    />
                    <div className="absolute top-2 left-2 pointer-events-none">
                      <span className="badge bg-emerald-500/80 text-white text-xs">
                        ▶️ {isZh ? 'AI 生成 demo' : 'AI demo'}
                      </span>
                    </div>
                  </div>
                ) : (
                  <div className="aspect-video bg-gradient-to-br from-slate-800 to-slate-900 relative overflow-hidden">
                    <div className="absolute inset-0 flex items-center justify-center text-6xl opacity-30 group-hover:opacity-50 transition">
                      {ch?.icon || '🎬'}
                    </div>
                    <div className="absolute top-2 left-2">
                      <span className="badge bg-black/60 text-white text-xs">
                        {ch?.icon} {pickLocalized(ch || {}, lang, 'name')}
                      </span>
                    </div>
                    <div className="absolute bottom-2 right-2">
                      <span className="badge bg-black/80 text-white text-xs font-mono">
                        {formatDuration(v.duration_sec)}
                      </span>
                    </div>
                    <div className="absolute top-2 right-2 flex gap-1">
                      {v.branches.filter(b => b !== 'ALL').slice(0, 2).map(bid => {
                        const b = CURRICULUM.branches[bid];
                        return b ? (
                          <span key={bid} className="badge bg-black/60 text-white text-xs">
                            {b.icon}
                          </span>
                        ) : null;
                      })}
                    </div>
                  </div>
                )}

                {/* 文字区 */}
                <div className="p-4">
                  <h3 className="text-sm font-bold mb-2 line-clamp-2 leading-snug">
                    {pickLocalized(v, lang, 'title')}
                  </h3>
                  <p className="text-xs text-slate-400 mb-3 line-clamp-2">
                    {v.cta}
                  </p>

                  {/* 关联题目 */}
                  {v.related_problems && v.related_problems.length > 0 && (
                    <div className="mb-3 flex flex-wrap gap-1">
                      {v.related_problems.slice(0, 3).map(pid => (
                        <Link
                          key={pid}
                          to={`/problems/${pid}`}
                          className="badge bg-violet-500/15 text-violet-200 text-xs hover:bg-violet-500/25"
                        >
                          → {pid.replace('millennium-', '').replace(/-/g, ' ').slice(0, 18)}
                        </Link>
                      ))}
                    </div>
                  )}

                  {/* 跳转 Academy 对应学科 (如果是单一学科) */}
                  {v.branches.length === 1 && v.branches[0] !== 'ALL' && (
                    <Link
                      to={`/academy/branch/${v.branches[0]}`}
                      className="btn-secondary w-full text-center text-xs py-2 mb-2"
                    >
                      🌳 {isZh ? `在 Academy 看 ${CURRICULUM.branches[v.branches[0]]?.name_zh}` : `View in Academy`}
                    </Link>
                  )}

                  {/* 视频号链接 */}
                  <a
                    href={v.wechat_video_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block w-full text-center text-xs py-2 bg-pink-500/20 hover:bg-pink-500/30 text-pink-200 rounded-lg border border-pink-500/30"
                  >
                    📱 {isZh ? '视频号看完整版' : 'Watch on WeChat'}
                  </a>

                  {/* 元数据 */}
                  <div className="mt-2 text-xs text-slate-500 flex items-center justify-between font-mono">
                    <span>L{v.levels.join('/')}</span>
                    <span>{v.published_at}</span>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </section>

      {/* 底部 CTA */}
      <section className="mt-8 card bg-gradient-to-br from-violet-500/10 to-cyan-500/10 border-violet-500/20 p-6 text-center">
        <div className="text-3xl mb-2">🎬 → 🎓</div>
        <h3 className="text-xl font-display font-bold mb-2">
          {isZh ? '看完了? 来 Academy 动手' : 'Watched? Now build on Academy'}
        </h3>
        <p className="text-sm text-slate-400 mb-4 max-w-2xl mx-auto">
          {isZh
            ? '视频号负责点燃好奇心, Academy 负责给你工具、题目和小组. 看了 1 分钟视频, 接下来 5 分钟自己在 Academy 问个问题.'
            : 'HP Hackathon ignites curiosity, Academy gives you tools, problems, and teams. Watch 1 min, then spend 5 min asking a question on Academy.'}
        </p>
        <div className="flex flex-wrap justify-center gap-3">
          <Link to="/academy" className="btn-primary">
            🎓 {isZh ? '进入学院' : 'Enter Academy'}
          </Link>
          <Link to="/academy/tree" className="btn-secondary">
            🌳 {isZh ? '看知识树' : 'See knowledge tree'}
          </Link>
          <Link to="/problems" className="btn-secondary">
            📚 {isZh ? '浏览 579 题' : 'Browse 579 problems'}
          </Link>
        </div>
      </section>
    </div>
  );
}
